import React from 'react';
import { Modal, Descriptions, Tag, Space, Row as AntRow, Col, Button } from 'antd';
import { Voucher } from '../../common/voucher.interface';
import { 
    GiftOutlined, 
    DollarOutlined, 
    CalendarOutlined, 
    EyeOutlined, 
    CloseOutlined,
    FileTextOutlined,
    UserOutlined,
    SettingOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';

interface CModalViewVoucherProps {
  visible: boolean;
  voucher: Voucher | null;
  onCancel: () => void;
}

const CModalViewVoucher: React.FC<CModalViewVoucherProps> = ({
  visible,
  voucher,
  onCancel,
}) => {
  if (!voucher) return null;

  const formatDate = (dateString: string) => {
    return dayjs(dateString).format('DD/MM/YYYY HH:mm:ss');
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('vi-VN', {
      style: 'currency',
      currency: 'VND'
    }).format(amount);
  };

  return (
    <Modal
      open={visible}
      onCancel={onCancel}
      title={
        <Space>
          <EyeOutlined />
          <span>Chi tiết voucher</span>
        </Space>
      }
      className="voucher-view-modal"
      footer={null}
      width={800}
      destroyOnClose
      maskClosable={true}
    >
      <div className="modal-content">
        <AntRow gutter={[24, 16]}>
          <Col span={12}>
            <div className="info-section">
              <h4>
                <Space>
                  <GiftOutlined style={{ fontSize: '14px', color: 'var(--primary-color)' }} />
                  <span>Thông tin cơ bản</span>
                </Space>
              </h4>
              <Descriptions column={1} size="small" className="voucher-descriptions">
                <Descriptions.Item 
                  label={
                    <Space>
                      <GiftOutlined style={{ fontSize: '12px', color: 'var(--primary-color)' }} />
                      <span>Mã voucher</span>
                    </Space>
                  }
                >
                  {voucher.code}
                </Descriptions.Item>
                <Descriptions.Item 
                  label={
                    <Space>
                      <FileTextOutlined style={{ fontSize: '12px', color: 'var(--primary-color)' }} />
                      <span>Tên voucher</span>
                    </Space>
                  }
                >
                  {voucher.name}
                </Descriptions.Item>
                <Descriptions.Item 
                  label={
                    <Space>
                      <FileTextOutlined style={{ fontSize: '12px', color: 'var(--primary-color)' }} />
                      <span>Mô tả</span>
                    </Space>
                  }
                >
                  {voucher.description}
                </Descriptions.Item>
                <Descriptions.Item 
                  label={
                    <Space>
                      <DollarOutlined style={{ fontSize: '12px', color: 'var(--primary-color)' }} />
                      <span>Loại voucher</span>
                    </Space>
                  }
                >
                  <Tag color={voucher.type === 'FREE' ? 'green' : 'blue'}>
                    {voucher.type === 'FREE' ? 'Miễn phí' : 'Trả phí'}
                  </Tag>
                </Descriptions.Item>
              </Descriptions>
            </div>
          </Col>

          <Col span={12}>
            <div className="info-section">
              <h4>
                <Space>
                  <DollarOutlined style={{ fontSize: '14px', color: 'var(--primary-color)' }} />
                  <span>Thông tin giảm giá</span>
                </Space>
              </h4>
              <Descriptions column={1} size="small" className="voucher-descriptions">
                <Descriptions.Item 
                  label={
                    <Space>
                      <DollarOutlined style={{ fontSize: '12px', color: 'var(--primary-color)' }} />
                      <span>Phần trăm giảm giá</span>
                    </Space>
                  }
                >
                  {voucher.discount}%
                </Descriptions.Item>
                <Descriptions.Item 
                  label={
                    <Space>
                      <DollarOutlined style={{ fontSize: '12px', color: 'var(--primary-color)' }} />
                      <span>Giảm tối đa</span>
                    </Space>
                  }
                >
                  {formatCurrency(voucher.maxDiscount || voucher.maxDiscountAmount || 0)}
                </Descriptions.Item>
                <Descriptions.Item 
                  label={
                    <Space>
                      <DollarOutlined style={{ fontSize: '12px', color: 'var(--primary-color)' }} />
                      <span>Đơn hàng tối thiểu</span>
                    </Space>
                  }
                >
                  {formatCurrency(voucher.minOrder || voucher.minOrderValue || 0)}
                </Descriptions.Item>
                {voucher.type === 'PAID' && (
                  <Descriptions.Item 
                    label={
                      <Space>
                        <UserOutlined style={{ fontSize: '12px', color: 'var(--primary-color)' }} />
                        <span>Điểm cần thiết</span>
                      </Space>
                    }
                  >
                    {voucher.pointsCost} điểm
                  </Descriptions.Item>
                )}
              </Descriptions>
            </div>
          </Col>

          <Col span={12}>
            <div className="info-section">
              <h4>
                <Space>
                  <UserOutlined style={{ fontSize: '14px', color: 'var(--primary-color)' }} />
                  <span>Thông tin số lượng</span>
                </Space>
              </h4>
              <Descriptions column={1} size="small" className="voucher-descriptions">
                <Descriptions.Item 
                  label={
                    <Space>
                      <UserOutlined style={{ fontSize: '12px', color: 'var(--primary-color)' }} />
                      <span>Tổng số lượng</span>
                    </Space>
                  }
                >
                  {voucher.quantity}
                </Descriptions.Item>
                <Descriptions.Item 
                  label={
                    <Space>
                      <UserOutlined style={{ fontSize: '12px', color: 'var(--primary-color)' }} />
                      <span>Đã sử dụng</span>
                    </Space>
                  }
                >
                  {voucher.usedCount || 0}
                </Descriptions.Item>
                <Descriptions.Item 
                  label={
                    <Space>
                      <UserOutlined style={{ fontSize: '12px', color: 'var(--primary-color)' }} />
                      <span>Còn lại</span>
                    </Space>
                  }
                >
                  {(voucher.quantity || 0) - (voucher.usedCount || 0)}
                </Descriptions.Item>
                <Descriptions.Item 
                  label={
                    <Space>
                      <SettingOutlined style={{ fontSize: '12px', color: 'var(--primary-color)' }} />
                      <span>Trạng thái</span>
                    </Space>
                  }
                >
                  <Tag color={voucher.isActive ? 'green' : 'red'}>
                    {voucher.isActive ? 'Hoạt động' : 'Tạm dừng'}
                  </Tag>
                </Descriptions.Item>
              </Descriptions>
            </div>
          </Col>

          <Col span={12}>
            <div className="info-section">
              <h4>
                <Space>
                  <CalendarOutlined style={{ fontSize: '14px', color: 'var(--primary-color)' }} />
                  <span>Thông tin thời gian</span>
                </Space>
              </h4>
              <Descriptions column={1} size="small" className="voucher-descriptions">
                <Descriptions.Item 
                  label={
                    <Space>
                      <CalendarOutlined style={{ fontSize: '12px', color: 'var(--primary-color)' }} />
                      <span>Ngày bắt đầu</span>
                    </Space>
                  }
                >
                  {formatDate(voucher.startDate?.toString() || '')}
                </Descriptions.Item>
                <Descriptions.Item 
                  label={
                    <Space>
                      <CalendarOutlined style={{ fontSize: '12px', color: 'var(--primary-color)' }} />
                      <span>Ngày kết thúc</span>
                    </Space>
                  }
                >
                  {formatDate(voucher.endDate?.toString() || '')}
                </Descriptions.Item>
                <Descriptions.Item 
                  label={
                    <Space>
                      <CalendarOutlined style={{ fontSize: '12px', color: 'var(--primary-color)' }} />
                      <span>Ngày tạo</span>
                    </Space>
                  }
                >
                  {voucher.createdAt ? formatDate(voucher.createdAt?.toString() || '') : 'N/A'}
                </Descriptions.Item>
                <Descriptions.Item 
                  label={
                    <Space>
                      <CalendarOutlined style={{ fontSize: '12px', color: 'var(--primary-color)' }} />
                      <span>Ngày cập nhật</span>
                    </Space>
                  }
                >
                  {voucher.updatedAt ? formatDate(voucher.updatedAt?.toString() || '') : 'N/A'}
                </Descriptions.Item>
              </Descriptions>
            </div>
          </Col>
        </AntRow>

        <div className="modal-actions">
          <Button
            onClick={onCancel}
            className="cancel-button"
            icon={<CloseOutlined />}
          >
            Đóng
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default CModalViewVoucher; 